import { type Language, t } from '../i18n';
import { ContactCTA } from './ContactCTA';
import { HeroStats } from './HeroStats';

interface ReportDisplayProps {
  language: Language;
  report: AssessmentReport | null;
  isLoading: boolean;
  onNewAssessment: () => void;
}

interface EconomicAnalysis {
  wasted_time: {
    hours_per_week: number;
    hours_per_year: number;
    fte_equivalent: number;
  };
  costs: {
    hourly_rate: number;
    weekly_cost: number;
    annual_cost: number;
    currency: string;
  };
  roi_potential: {
    estimated_savings_percent: number;
    estimated_annual_savings: number;
    payback_months: number;
  };
  data_source: 'collected' | 'estimated';
}

interface AssessmentReport {
  metadata: {
    assessment_id: string;
    assessment_date: string;
    company_profile: {
      industry: string;
      size_employees: number;
      primary_region?: string;
    };
  };
  executive_summary?: string[];
  scores: {
    raw_total: number;
    adjusted_total: number;
    categories: {
      process_maturity: CategoryScore;
      data_readiness: CategoryScore;
      technology: CategoryScore;
      organization: CategoryScore;
      strategy: CategoryScore;
    };
  };
  metrics: {
    maturity_score?: number;
    maturity_rating?: string;
    sustainability_score?: number;  // backward compat with old reports
    sustainability_rating?: string;
    optimization_potential_percent: number;
    unexpressed_value: {
      amount: number;
      currency: string;
    };
    competitive_position: {
      regional: { percentile: number; interpretation: string };
      industry: { percentile: number; interpretation: string };
    };
    risk_level: string;
  };
  economic_analysis?: EconomicAnalysis;
  opportunities: Array<{
    rank: number;
    area: string;
    description: string;
    complexity: string;
    priority: string;
  }>;
  recommendations: {
    immediate_actions: Array<{
      action: string;
      rationale: string;
      timeline?: string;
    }>;
    phases: {
      phase_1: Phase;
      phase_2: Phase;
      phase_3: Phase;
    };
    key_risks: Array<{
      risk: string;
      likelihood: string;
      impact: string;
      mitigation: string;
    }>;
  };
  summary: {
    headline: string;
    key_strengths: string[];
    key_gaps: string[];
    primary_opportunity: string;
    next_step: string;
  };
}

interface CategoryScore {
  score: number;
  max: number;
  percentage: number;
  rating: string;
}

interface Phase {
  name: string;
  duration: string;
  focus: string;
  initiatives?: string[];
  expected_outcomes?: string[];
}

function ScoreGauge({ score, max = 100 }: { score: number; max?: number }) {
  const percentage = (score / max) * 100;
  const circumference = 2 * Math.PI * 35;
  const offset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (score >= 80) return '#22c55e';
    if (score >= 65) return '#3b82f6';
    if (score >= 50) return '#f59e0b';
    if (score >= 35) return '#f97316';
    return '#ef4444';
  };

  return (
    <div
      className="relative w-24 h-24"
      role="img"
      aria-label={`Score: ${score} out of ${max}, ${percentage.toFixed(0)}%`}
    >
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r="35"
          className="stroke-muted"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r="35"
          stroke={getColor()}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="score-gauge"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
        <span className="text-2xl font-bold text-foreground">{score}</span>
        <span className="text-xs text-muted-foreground">/ {max}</span>
      </div>
    </div>
  );
}

function CategoryBar({ label, score, max, percentage, animate = false, delay = 0 }: { label: string; score: number; max: number; percentage: number; animate?: boolean; delay?: number }) {
  const getColor = () => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-foreground w-20 truncate" id={`${label.replace(/\s+/g, '-').toLowerCase()}-label`}>
        {label}
      </span>
      <div
        className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-labelledby={`${label.replace(/\s+/g, '-').toLowerCase()}-label`}
        aria-valuetext={`${score} of ${max} points, ${percentage.toFixed(0)}%`}
      >
        <div
          className={`h-full ${getColor()} rounded-full transition-all duration-700 ${animate ? 'animate-bar-fill' : ''}`}
          style={{
            width: animate ? `${percentage}%` : `${percentage}%`,
            animationDelay: animate ? `${delay}ms` : '0ms'
          }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right" aria-hidden="true">{score}/{max}</span>
    </div>
  );
}

function formatCurrency(amount: number, currency: string, locale?: string): string {
  return new Intl.NumberFormat(locale ?? 'it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ReportDisplay({ language, report, isLoading, onNewAssessment }: ReportDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20" role="status" aria-busy="true">
        <div className="loading-dots text-primary text-2xl mb-4" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p className="text-muted-foreground">{t(language, 'report.generating')}</p>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessment-${report.metadata.assessment_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate potential savings for hero section
  const potentialSavings = report.economic_analysis?.roi_potential?.estimated_annual_savings
    ?? Math.round(report.metrics.unexpressed_value.amount);
  const currency = report.economic_analysis?.costs?.currency ?? report.metrics.unexpressed_value.currency;

  return (
    <div className="max-w-5xl mx-auto space-y-3">
      {/* Hero Stats - WOW effect */}
      <HeroStats
        potentialSavings={potentialSavings}
        currency={currency}
        optimizationPercent={Math.round(report.metrics.optimization_potential_percent)}
        language={language}
      />

      {/* Executive Summary */}
      {report.executive_summary && report.executive_summary.length > 0 && (
        <div className="card p-4 border-l-4 border-l-primary bg-primary/5">
          <h2 className="text-sm font-semibold text-primary mb-2">
            {language === 'it' ? 'Sintesi Esecutiva' : 'Executive Summary'}
          </h2>
          <ul className="space-y-1.5">
            {report.executive_summary.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <span className="text-primary font-bold mt-0.5">-</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">
            {t(language, 'report.title')}
          </h1>
          <p className="text-xs text-muted-foreground">
            {report.metadata.assessment_date} | {t(language, `form.industry.options.${report.metadata.company_profile.industry}`) !== `form.industry.options.${report.metadata.company_profile.industry}` ? t(language, `form.industry.options.${report.metadata.company_profile.industry}`) : report.metadata.company_profile.industry}
          </p>
        </div>
        <p className="text-sm text-foreground max-w-md text-right">{report.summary.headline}</p>
      </div>

      {/* Score, Metrics, and Categories - All in one row */}
      <div className="grid grid-cols-3 gap-3">
        {/* Score Gauge */}
        <div className="card p-3 flex items-center gap-3">
          <ScoreGauge score={report.metrics.maturity_score ?? report.metrics.sustainability_score ?? 0} />
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t(language, 'report.score.title')}</span>
            <span
              className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                (report.metrics.maturity_score ?? report.metrics.sustainability_score ?? 0) >= 65
                  ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : (report.metrics.maturity_score ?? report.metrics.sustainability_score ?? 0) >= 50
                  ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                  : 'bg-red-500/10 text-red-600 dark:text-red-400'
              }`}
            >
              {report.metrics.maturity_rating ?? report.metrics.sustainability_rating}
            </span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="card p-3">
          <h2 className="text-xs font-semibold text-muted-foreground mb-2">
            {t(language, 'report.metrics.title')}
          </h2>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t(language, 'report.metrics.optimization')}</span>
              <span className="font-semibold text-foreground">{report.metrics.optimization_potential_percent.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t(language, 'report.metrics.unexpressed')}</span>
              <span className="font-semibold text-primary">
                {formatCurrency(report.metrics.unexpressed_value.amount, report.metrics.unexpressed_value.currency)}/{language === 'it' ? 'anno' : 'yr'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t(language, 'report.metrics.regional')}</span>
              <span className="font-semibold text-foreground">{report.metrics.competitive_position.regional.interpretation}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t(language, 'report.metrics.risk')}</span>
              <span className="font-semibold text-foreground">{report.metrics.risk_level}</span>
            </div>
          </div>
        </div>

        {/* Category Scores - Moved here */}
        <div className="card p-3">
          <h2 className="text-xs font-semibold text-muted-foreground mb-2">
            {t(language, 'report.categories.title')}
          </h2>
          <div className="space-y-1.5">
            <CategoryBar
              label={t(language, 'report.categories.process')}
              score={report.scores.categories.process_maturity.score}
              max={report.scores.categories.process_maturity.max}
              percentage={report.scores.categories.process_maturity.percentage}
            />
            <CategoryBar
              label={t(language, 'report.categories.data')}
              score={report.scores.categories.data_readiness.score}
              max={report.scores.categories.data_readiness.max}
              percentage={report.scores.categories.data_readiness.percentage}
            />
            <CategoryBar
              label={t(language, 'report.categories.technology')}
              score={report.scores.categories.technology.score}
              max={report.scores.categories.technology.max}
              percentage={report.scores.categories.technology.percentage}
            />
            <CategoryBar
              label={t(language, 'report.categories.organization')}
              score={report.scores.categories.organization.score}
              max={report.scores.categories.organization.max}
              percentage={report.scores.categories.organization.percentage}
            />
            <CategoryBar
              label={t(language, 'report.categories.strategy')}
              score={report.scores.categories.strategy.score}
              max={report.scores.categories.strategy.max}
              percentage={report.scores.categories.strategy.percentage}
            />
          </div>
        </div>
      </div>

      {/* Economic Analysis - Wasted Time & Cost */}
      {report.economic_analysis && (
        <div className="card p-3 bg-gradient-to-r from-red-500/10 to-orange-500/10 dark:from-red-500/20 dark:to-orange-500/20 border-red-500/30">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold text-red-600 dark:text-red-400">
              {t(language, 'report.economic.title')}
            </h2>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              report.economic_analysis.data_source === 'collected'
                ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
            }`}>
              {report.economic_analysis.data_source === 'collected'
                ? t(language, 'report.economic.collected')
                : t(language, 'report.economic.estimated')}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {/* Wasted Hours */}
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {report.economic_analysis.wasted_time.hours_per_week}
              </div>
              <div className="text-xs text-muted-foreground">{t(language, 'report.economic.hoursWeek')}</div>
              <div className="text-xs text-muted-foreground/70">
                ({report.economic_analysis.wasted_time.hours_per_year.toLocaleString()} {t(language, 'report.economic.hoursYear')})
              </div>
            </div>
            {/* FTE Equivalent */}
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {report.economic_analysis.wasted_time.fte_equivalent.toFixed(1)}
              </div>
              <div className="text-xs text-muted-foreground">{t(language, 'report.economic.fte')}</div>
              <div className="text-xs text-muted-foreground/70">{t(language, 'report.economic.fteDesc')}</div>
            </div>
            {/* Annual Cost */}
            <div className="text-center">
              <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                {formatCurrency(report.economic_analysis.costs.annual_cost, report.economic_analysis.costs.currency)}
              </div>
              <div className="text-xs text-muted-foreground">{t(language, 'report.economic.annualCost')}</div>
              <div className="text-xs text-muted-foreground/70">
                @{formatCurrency(report.economic_analysis.costs.hourly_rate, report.economic_analysis.costs.currency)}/{language === 'it' ? 'ora' : 'h'}
              </div>
            </div>
            {/* ROI Potential */}
            <div className="text-center bg-green-500/10 dark:bg-green-500/20 rounded-lg p-2 -m-2">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(report.economic_analysis.roi_potential.estimated_annual_savings, report.economic_analysis.costs.currency)}
              </div>
              <div className="text-xs text-muted-foreground">{t(language, 'report.economic.savings')}</div>
              <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                {report.economic_analysis.roi_potential.estimated_savings_percent}% | {report.economic_analysis.roi_potential.payback_months} {t(language, 'report.economic.months')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opportunities - Compact grid */}
      <div className="card p-3">
        <h2 className="text-xs font-semibold text-muted-foreground mb-2">
          {t(language, 'report.opportunities.title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {report.opportunities.slice(0, 6).map((opp) => (
            <div key={opp.rank} className="flex items-start gap-2 p-2 bg-muted rounded">
              <span className="flex-shrink-0 w-5 h-5 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-semibold">
                {opp.rank}
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-medium text-foreground truncate">{opp.area}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2">{opp.description}</p>
                <span className="text-xs px-1.5 py-0.5 bg-primary/20 text-primary rounded mt-1 inline-block">
                  {opp.priority}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations - Two columns */}
      <div className="grid grid-cols-2 gap-3">
        {/* Immediate Actions */}
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">
            {t(language, 'report.recommendations.immediate')}
          </h3>
          <div className="space-y-1.5">
            {report.recommendations.immediate_actions.slice(0, 3).map((action, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-primary text-xs">→</span>
                <div>
                  <p className="text-xs font-medium text-foreground">{action.action}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{action.rationale}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Phases - Visual Timeline */}
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-muted-foreground mb-4">
            {t(language, 'report.recommendations.phases')}
          </h3>

          {/* Timeline Visualization */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 rounded-full" />

            {/* Phase Nodes */}
            <div className="relative flex justify-between mb-4">
              {/* Phase 1 */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold shadow-lg shadow-green-500/30 z-10">
                  1
                </div>
                <span className="mt-2 text-xs font-semibold text-green-600 dark:text-green-400">
                  {language === 'it' ? 'Azioni Rapide' : 'Quick Wins'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {report.recommendations.phases.phase_1.duration}
                </span>
              </div>

              {/* Phase 2 */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/30 z-10">
                  2
                </div>
                <span className="mt-2 text-xs font-semibold text-blue-600 dark:text-blue-400">
                  {language === 'it' ? 'Fondazione' : 'Foundation'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {report.recommendations.phases.phase_2.duration}
                </span>
              </div>

              {/* Phase 3 */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold shadow-lg shadow-purple-500/30 z-10">
                  3
                </div>
                <span className="mt-2 text-xs font-semibold text-purple-600 dark:text-purple-400">
                  {language === 'it' ? 'Ottimizzazione' : 'Optimization'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {report.recommendations.phases.phase_3.duration}
                </span>
              </div>
            </div>

            {/* Phase Details Cards */}
            <div className="grid grid-cols-3 gap-2 mt-2">
              {/* Phase 1 Card */}
              <div className="p-2 bg-green-500/5 dark:bg-green-500/10 border border-green-500/20 rounded-lg">
                <h4 className="font-semibold text-foreground text-xs mb-1">
                  {report.recommendations.phases.phase_1.name}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {report.recommendations.phases.phase_1.focus}
                </p>
                <ul className="space-y-0.5">
                  {report.recommendations.phases.phase_1.initiatives?.slice(0, 2).map((init, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                      <span className="text-green-500 flex-shrink-0">-</span>
                      <span className="line-clamp-1">{init}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 2 Card */}
              <div className="p-2 bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <h4 className="font-semibold text-foreground text-xs mb-1">
                  {report.recommendations.phases.phase_2.name}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {report.recommendations.phases.phase_2.focus}
                </p>
                <ul className="space-y-0.5">
                  {report.recommendations.phases.phase_2.initiatives?.slice(0, 2).map((init, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                      <span className="text-blue-500 flex-shrink-0">-</span>
                      <span className="line-clamp-1">{init}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 3 Card */}
              <div className="p-2 bg-purple-500/5 dark:bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <h4 className="font-semibold text-foreground text-xs mb-1">
                  {report.recommendations.phases.phase_3.name}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {report.recommendations.phases.phase_3.focus}
                </p>
                <ul className="space-y-0.5">
                  {report.recommendations.phases.phase_3.initiatives?.slice(0, 2).map((init, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                      <span className="text-purple-500 flex-shrink-0">-</span>
                      <span className="line-clamp-1">{init}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary - Compact with inline next step */}
      <div className="card p-3">
        <div className="flex gap-4">
          <div className="flex-1">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1">
              {t(language, 'report.summary.strengths')}
            </h3>
            <ul className="space-y-0.5">
              {report.summary.key_strengths.slice(0, 3).map((s, i) => (
                <li key={i} className="flex items-start gap-1 text-xs text-foreground">
                  <span className="text-green-500">✓</span>
                  <span className="line-clamp-1">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1">
              {t(language, 'report.summary.gaps')}
            </h3>
            <ul className="space-y-0.5">
              {report.summary.key_gaps.slice(0, 3).map((g, i) => (
                <li key={i} className="flex items-start gap-1 text-xs text-foreground">
                  <span className="text-orange-500">!</span>
                  <span className="line-clamp-1">{g}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1 p-2 bg-primary/10 rounded">
            <h3 className="text-xs font-medium text-primary mb-1">
              {t(language, 'report.summary.nextStep')}
            </h3>
            <p className="text-xs text-foreground line-clamp-3">{report.summary.next_step}</p>
          </div>
        </div>
      </div>

      {/* Actions - Compact */}
      <div className="flex gap-3 justify-center" role="group" aria-label={t(language, 'report.actions')}>
        <button
          onClick={handleDownload}
          className="btn-secondary text-sm py-1.5 px-3"
          aria-label={t(language, 'report.download')}
        >
          {t(language, 'report.download')}
        </button>
        <button
          onClick={onNewAssessment}
          className="btn-primary text-sm py-1.5 px-3"
          aria-label={t(language, 'report.newAssessment')}
        >
          {t(language, 'report.newAssessment')}
        </button>
      </div>

      {/* CTA Section */}
      <ContactCTA
        language={language}
        assessmentId={report.metadata.assessment_id}
      />
    </div>
  );
}
